import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { Switches } from "../Switches/Switches";
import styles from "./Dropdown.module.css";

const DROPDOWN_TYPES = {
  COUNTRY: "country",
  DROPDOWN: "dropdown",
  DEFAULT_DROPDOWN: "default-dropdown",
  NETWORK_DROPDOWN: "network-dropdown",
  SEARCH_DROPDOWN: "search-dropdown",
  SIMPLE_DROPDOWN: "simple-dropdown",
};

/**
 * Dropdown Component
 * A flexible dropdown component that supports multiple types and use cases
 */
export const Dropdown = ({
  type = DROPDOWN_TYPES.DROPDOWN,
  customStyles,
  className,
  ariaLabel,
  countryData,
  handlerClick,
  coutnryCode,
  dropdown = "dropdown",
  data,
  handleListItemClick,
  active,
  defaultOption,
  selectHandler,
  onChangeDropdown,
  autoFocus = false,
}) => {
  // Handle item click with proper event handling
  const handleItemClick = useCallback(
    (e, subItem, isInteractiveComponent) => {
      // Don't trigger parent onClick if clicking on interactive child components
      if (
        e.target.closest('[class*="switcherContainerHidden"]') ||
        e.target.closest('[class*="dropdownItemHidden"]') ||
        e.target.closest('[class*="dropdownMenuHidden"]')
      ) {
        return;
      }

      // For interactive components, only trigger if clicking outside the component
      if (!isInteractiveComponent && subItem.onClick) {
        if (typeof subItem.onClick === "function") {
          subItem.onClick();
        }
        // Call handleListItemClick to allow parent to close dropdown
        if (handleListItemClick) {
          handleListItemClick(subItem);
        }
      }
    },
    [handleListItemClick]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e, subItem, isInteractiveComponent) => {
      if ((e.key === "Enter" || e.key === " ") && !isInteractiveComponent && subItem.onClick) {
        e.preventDefault();
        if (typeof subItem.onClick === "function") {
          subItem.onClick();
        }
        if (handleListItemClick) {
          handleListItemClick(subItem);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (handleListItemClick) {
          handleListItemClick(null);
        }
      }
    },
    [handleListItemClick]
  );

  let element = "";

  if (type === DROPDOWN_TYPES.COUNTRY) {
    element = (
      <div style={customStyles} className={styles.dropdownCountry} role="menu" aria-label="Country selection">
        <h2 className={styles.dropdownToggle} role="presentation">Select Country</h2>
        {countryData?.map((item, index) => (
          <div key={index} className={styles.dropdownMenuCountry}>
            <div
              onClick={() =>
                handlerClick?.({
                  flag: item.flag,
                  country: item.country,
                  code: item.code,
                })
              }
              className={styles.dropdownItemCountry}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlerClick?.({
                    flag: item.flag,
                    country: item.country,
                    code: item.code,
                  });
                }
              }}
              aria-label={`Select ${item.country}`}
            >
              <p>{item.flag}</p>
              <p>{item.country}</p>
              {coutnryCode && <p>{item.code}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === DROPDOWN_TYPES.DROPDOWN) {
    element = (
      <div
        style={customStyles}
        className={`${styles.active} ${styles.dropdown} dropdown ${className || ""}`}
        role="menu"
        aria-label={ariaLabel}
      >
        {data?.map((item, index) => (
          <div key={item.id || index} role="group">
            {item.title && (
              <h2
                onClick={() => handlerClick?.(item.title)}
                className={`${styles.dropdownToggle} dropdown-toggle`}
                role="presentation"
              >
                {item.title}
              </h2>
            )}
            {item.list?.map((subItem, subIndex) => {
              const isInteractiveComponent = React.isValidElement(subItem.title);
              const itemId = subItem.id || subIndex;
              const isActive = active === `${itemId}`;

              return (
                <div
                  key={itemId}
                  className={`${styles.dropdownItem} dropdown-item`}
                  role="menuitem"
                  tabIndex={0}
                  onClick={(e) => handleItemClick(e, subItem, isInteractiveComponent)}
                  onKeyDown={(e) => handleKeyDown(e, subItem, isInteractiveComponent)}
                  aria-label={typeof subItem.title === "string" ? subItem.title : undefined}
                >
                  {subItem.svg && (
                    <span className={styles.dropdownItemIcon} aria-hidden="true">
                      {subItem.svg}
                    </span>
                  )}
                  <div className={styles.subitemTitleAndSwitch}>
                    <div
                      id={itemId}
                      className={`${isActive ? styles.leftLine : ""} ${styles.dropdownItemColor}`}
                    >
                      {subItem.title}
                    </div>
                    {subItem.togle === true && (
                      <Switches onChange={() => subItem.onClick} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  if (type === DROPDOWN_TYPES.DEFAULT_DROPDOWN) {
    element = (
      <div style={customStyles} className={`${styles.active} ${styles.dropdown} dropdown`} role="menu">
        {defaultOption && (
          <div
            className={`${styles.dropdownItem} dropdown-item`}
            style={{ display: "none" }}
            onClick={() => {
              handlerClick?.(defaultOption);
              selectHandler?.("all");
            }}
            key={defaultOption}
            role="menuitem"
            aria-hidden="true"
          >
            {defaultOption}
          </div>
        )}
        {data?.map((item, index) => (
          <div
            className={`${styles.dropdownItem} dropdown-item`}
            onClick={() => {
              handlerClick?.(item.name);
              selectHandler?.(item.value);
            }}
            key={index}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlerClick?.(item.name);
                selectHandler?.(item.value);
              }
            }}
            aria-label={item.name}
          >
            {item?.svg && <span aria-hidden="true">{item.svg}</span>}
            {item.name}
          </div>
        ))}
      </div>
    );
  }

  if (type === DROPDOWN_TYPES.NETWORK_DROPDOWN) {
    element = (
      <div style={customStyles} className={`${styles.active} ${styles.dropdown} dropdown`} role="menu" aria-label="Network selection">
        {data?.map((item, index) => (
          <div
            className={`${styles.dropdownItem} dropdown-item`}
            onClick={() => {
              handlerClick?.(item.networkName);
              selectHandler?.({
                networkName: item.networkName,
                tokenStandard: item.tokenStandard,
              });
            }}
            key={index}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlerClick?.(item.networkName);
                selectHandler?.({
                  networkName: item.networkName,
                  tokenStandard: item.tokenStandard,
                });
              }
            }}
            aria-label={item.networkName}
          >
            {item?.svg && <span aria-hidden="true">{item.svg}</span>}
            {item.networkName}
          </div>
        ))}
      </div>
    );
  }

  if (type === DROPDOWN_TYPES.SEARCH_DROPDOWN) {
    element = (
      <div style={customStyles} className={`${styles.active} ${styles.dropdown} ${styles.searchDropdown} dropdown search-dropdown`} role="menu">
        <input
          type="text"
          className={styles.dropdownSearchInput}
          onChange={onChangeDropdown}
          autoFocus={autoFocus}
          placeholder="Type code to filter…"
          aria-label="Search filter"
        />
        {defaultOption && (
          <div
            className={`${styles.dropdownItem} dropdown-item`}
            onClick={() => {
              handlerClick?.(defaultOption);
              selectHandler?.("all");
            }}
            key={defaultOption}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlerClick?.(defaultOption);
                selectHandler?.("all");
              }
            }}
            aria-label={defaultOption}
          >
            {defaultOption}
          </div>
        )}
        {data?.map((item, index) => (
          <div
            className={`${styles.dropdownItem} dropdown-item`}
            onClick={() => {
              handlerClick?.(item.name);
              selectHandler?.(item.value);
            }}
            key={index}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlerClick?.(item.name);
                selectHandler?.(item.value);
              }
            }}
            aria-label={item.name}
          >
            {item.name}
          </div>
        ))}
      </div>
    );
  }

  if (type === DROPDOWN_TYPES.SIMPLE_DROPDOWN) {
    element = (
      <div className={`${styles.dropdown} dropdown`} style={customStyles} role="menu">
        {data?.map((item, index) => (
          <div
            onClick={() => handlerClick?.({ name: item.name, img: item.img })}
            className={`${styles.dropdownItem} dropdown-item`}
            key={index}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handlerClick?.({ name: item.name, img: item.img });
              }
            }}
            aria-label={item.name}
          >
            {item.img && <span aria-hidden="true">{item.img}</span>}
            {item.name}
          </div>
        ))}
      </div>
    );
  }

  return element;
};

Dropdown.propTypes = {
  type: PropTypes.oneOf(Object.values(DROPDOWN_TYPES)),
  customStyles: PropTypes.object,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  countryData: PropTypes.array,
  handlerClick: PropTypes.func,
  coutnryCode: PropTypes.bool,
  dropdown: PropTypes.string,
  data: PropTypes.array,
  handleListItemClick: PropTypes.func,
  active: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultOption: PropTypes.string,
  selectHandler: PropTypes.func,
  onChangeDropdown: PropTypes.func,
  autoFocus: PropTypes.bool,
};

Dropdown.defaultProps = {
  type: DROPDOWN_TYPES.DROPDOWN,
  customStyles: undefined,
  className: "",
  ariaLabel: undefined,
  countryData: undefined,
  handlerClick: undefined,
  coutnryCode: false,
  dropdown: "dropdown",
  data: undefined,
  handleListItemClick: undefined,
  active: undefined,
  defaultOption: undefined,
  selectHandler: undefined,
  onChangeDropdown: undefined,
  autoFocus: false,
};

export default memo(Dropdown);
